import type { PersistableQueryKey } from './useFilterDefaults';

// Pure halves of useFilterDefaults (no router, no auth), so they're testable on their own.

/**
 * Normalizes a path by removing dynamic route segments, so related pages share one set of defaults
 * (e.g. '/teams/active/user/[userId]/[section]' -> '/teams/active/user', '/pipeline/[stageId]' -> '/pipeline').
 */
function normalizePath(pathname: string): string {
  return pathname
    .split('/')
    .filter((segment) => !segment.startsWith('['))
    .join('/');
}

/**
 * Pages that keep their own defaults instead of sharing their parent's. The campaign page shared the
 * campaigns list's: there `userId` is the campaign owner, here it's the Enrolled contact owner, so
 * someone else's campaign opened filtered to "<you> (Me)" and empty.
 */
const OWN_DEFAULTS_PAGES: ReadonlySet<string> = new Set(['/templates/[templateId]']);

/** Keys that meant something else under a page's old shared defaults, so they're not carried over. */
const NOT_CARRIED_FROM_SHARED: readonly string[] = ['userId'];

const keyFor = (teamId: string, path: string, impersonatedUserId: string | null) => {
  const base = `filterDefaults:${teamId}:${path}`;
  return impersonatedUserId == null ? base : `${base}:as:${impersonatedUserId}`;
};

/**
 * Gets the localStorage key for filter defaults.
 *
 * Scoped by impersonated user so a superadmin impersonating different people
 * (or nobody) keeps a separate set of saved defaults per identity. Without this,
 * impersonating person B on the same team + page would inherit person A's
 * filters. `null` = not impersonating (the superadmin's own bucket).
 */
export function getStorageKey(teamId: string, pathname: string, impersonatedUserId: string | null): string {
  return keyFor(teamId, OWN_DEFAULTS_PAGES.has(pathname) ? pathname : normalizePath(pathname), impersonatedUserId);
}

/**
 * The page's saved defaults (raw JSON), or null. A page that now keeps its own defaults falls back,
 * once, to the shared set it used to read — minus the keys that meant something else there — and
 * copies that into its own key, so nobody's saved filters vanish on deploy.
 */
export function readSavedDefaults(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  teamId: string,
  pathname: string,
  impersonatedUserId: string | null,
): string | null {
  const key = getStorageKey(teamId, pathname, impersonatedUserId);
  const own = storage.getItem(key);
  if (own != null || !OWN_DEFAULTS_PAGES.has(pathname)) return own;
  const shared = storage.getItem(keyFor(teamId, normalizePath(pathname), impersonatedUserId));
  if (shared == null) return null;
  let carried: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(shared);
    if (typeof parsed === 'object' && parsed != null) {
      carried = Object.fromEntries(Object.entries(parsed).filter(([k]) => !NOT_CARRIED_FROM_SHARED.includes(k)));
    }
  } catch {
    // An unreadable shared set carries nothing.
  }
  const migrated = JSON.stringify(carried);
  storage.setItem(key, migrated);
  return migrated;
}

/**
 * Drop some keys from a page's saved defaults — for a control outside the filter bar that clears a
 * filter (an empty state's "Clear filter"), so the next visit doesn't bring it straight back.
 */
export function forgetSavedFilters(storageKey: string, keys: readonly PersistableQueryKey[]): void {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return;
    const parsed: unknown = JSON.parse(saved);
    if (typeof parsed !== 'object' || parsed == null) return;
    const next = Object.fromEntries(Object.entries(parsed).filter(([k]) => !keys.some((key) => key === k)));
    localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Ignore localStorage errors (private browsing, etc.)
  }
}
